import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VehicleCard from "@/components/VehicleCard";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const typeFilter = params.get("type") || "all";
  const categoryFilter = params.get("category") || "all";
  const transmissionFilter = params.get("transmission") || "all";
  const sort = params.get("sort") || "default";

  useEffect(() => {
    document.title = "Vehicle Fleet — Car & Motorbike Rental Puerto Princesa | PB-Lux";
    setLoading(true);
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")),
      { threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [vehicles.length]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (!value || value === "all") p.delete(key);
    else p.set(key, value);
    setParams(p);
  };

  const filtered = useMemo(() => {
    let list = [...vehicles];
    if (typeFilter !== "all") list = list.filter((v) => v.type === typeFilter);
    if (categoryFilter !== "all") list = list.filter((v) => v.category === categoryFilter);
    if (transmissionFilter !== "all") list = list.filter((v) => v.transmission === transmissionFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) => v.name.toLowerCase().includes(q) || v.brand.toLowerCase().includes(q) || v.model.toLowerCase().includes(q)
      );
    }
    if (sort === "price-asc") list.sort((a, b) => a.daily_rate - b.daily_rate);
    if (sort === "price-desc") list.sort((a, b) => b.daily_rate - a.daily_rate);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [vehicles, typeFilter, categoryFilter, transmissionFilter, search, sort]);

  return (
    <div className="min-h-screen relative">
      <Navbar />

      {/* Header */}
      <section className="hero-bg pt-36 pb-14 relative noise" data-testid="catalog-hero">
        <div className="max-w-7xl mx-auto px-6">
          <p className="kicker">The Collection</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4">
            Explore the <em className="not-italic text-gold-gradient">Fleet</em>
          </h1>
          <p className="mt-4 text-foreground/65 max-w-2xl">
            Cars, SUVs, vans, and motorbikes — every vehicle inspected, polished, and ready for your Palawan journey.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 glass-dark border-y border-gold-500/10" data-testid="catalog-filters">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gold-500/70" />
            <Input
              placeholder="Search by name or brand…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="catalog-search"
              className="bg-transparent border-gold-500/20 text-foreground placeholder:text-foreground/40 focus-visible:ring-gold-500/40"
            />
          </div>

          <Select value={typeFilter} onValueChange={(v) => setParam("type", v)}>
            <SelectTrigger className="w-[140px] bg-transparent border-gold-500/20 text-foreground" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="car">Cars</SelectItem>
              <SelectItem value="motorbike">Motorbikes</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={(v) => setParam("category", v)}>
            <SelectTrigger className="w-[150px] bg-transparent border-gold-500/20 text-foreground" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="hatchback">Hatchback</SelectItem>
              <SelectItem value="sedan">Sedan</SelectItem>
              <SelectItem value="mpv">MPV (7-seater)</SelectItem>
              <SelectItem value="suv">SUV</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="van">Van</SelectItem>
              <SelectItem value="scooter">Scooter</SelectItem>
            </SelectContent>
          </Select>

          <Select value={transmissionFilter} onValueChange={(v) => setParam("transmission", v)}>
            <SelectTrigger className="w-[150px] bg-transparent border-gold-500/20 text-foreground" data-testid="filter-transmission">
              <SelectValue placeholder="Transmission" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="automatic">Automatic</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-[160px] bg-transparent border-gold-500/20 text-foreground" data-testid="filter-sort">
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1 text-gold-500/70" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
              <SelectItem value="name">Name (A-Z)</SelectItem>
            </SelectContent>
          </Select>

          <p className="text-xs text-foreground/50 ml-auto" data-testid="result-count">
            {filtered.length} {filtered.length === 1 ? "vehicle" : "vehicles"}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="section-bg-dark py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-ink-800 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="no-results">
              <p className="font-display text-3xl text-foreground/60">No vehicles match your filters.</p>
              <p className="mt-3 text-sm text-foreground/40">Try clearing some filters above.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="catalog-grid">
              {filtered.map((v, i) => (
                <div key={v.id} className="reveal" style={{ transitionDelay: `${i * 50}ms` }}>
                  <VehicleCard vehicle={v} index={i} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
